import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import StandardTable from "../../../ui/components/StandardTable";
import {
  FILLING_RATE_OBJECTIVE,
  HIGH_IMPACT_GAP_PERCENT,
  INSTABILITY_VARIATION_PERCENT,
} from "../utils/collectionInsights";
import { VALORIZATION_RECO_THRESHOLDS } from "../utils/buildValorizationRecommendations";
import {
  VALORIZATION_FORMULA_ROWS,
  VALORIZATION_RECOMMENDATION_CATALOG,
} from "../utils/valorizationRecommendationTranslationKeys";

const SEVERITY_STYLES = {
  high: "bg-light-grey text-status-red-danger",
  medium: "bg-light-grey text-status-orange",
  watch: "bg-light-grey text-grey",
};

const SEVERITY_LABELS = {
  high: "Impact level.high",
  medium: "Impact level.medium",
  watch: "To monitor",
};

const getRotationRecommendationTypeRows = (t) => {
  const objective = FILLING_RATE_OBJECTIVE;
  const highGap = HIGH_IMPACT_GAP_PERCENT;
  const mediumFloor = objective - highGap;
  const instability = INSTABILITY_VARIATION_PERCENT;

  return [
    {
      id: "under-objective-high",
      severity: "high",
      scenarioTitle: t("recommendationType.high.scenarioTitle"),
      scenarioDescription: t("recommendationType.high.scenarioDescription"),
      criterion: t("recommendationType.high.criterion", { gap: highGap }),
      example: t("recommendationType.high.example", {
        objective,
        threshold: mediumFloor,
      }),
      recommendation: t("recommendationType.high.recommendation"),
    },
    {
      id: "under-objective-medium",
      severity: "medium",
      scenarioTitle: t("recommendationType.medium.scenarioTitle"),
      scenarioDescription: null,
      criterion: t("recommendationType.medium.criterion", { gap: highGap }),
      example: t("recommendationType.medium.example", {
        objective,
        from: mediumFloor,
        to: objective,
      }),
      recommendation: t("recommendationType.medium.recommendation"),
    },
    {
      id: "instability",
      severity: "watch",
      scenarioTitle: t("recommendationType.instability.scenarioTitle"),
      scenarioDescription: t(
        "recommendationType.instability.scenarioDescription",
      ),
      criterion: t("recommendationType.instability.criterion", {
        variation: instability,
      }),
      example: t("recommendationType.instability.example"),
      recommendation: t("recommendationType.instability.recommendation"),
    },
    {
      id: "auto-collection",
      severity: "watch",
      scenarioTitle: t("recommendationType.autoCollection.scenarioTitle"),
      scenarioDescription: t(
        "recommendationType.autoCollection.scenarioDescription",
      ),
      criterion: t("recommendationType.autoCollection.criterion"),
      example: null,
      recommendation: t("recommendationType.autoCollection.recommendation"),
    },
  ];
};

const getValorizationRecommendationTypeRows = (t) => {
  const {
    SORTING_ERROR_HIGH,
    SHARE_HIGH_IMPACT,
    SORTING_GAIN_HIGH,
    OBJECTIVE_GAP_HIGH,
  } = VALORIZATION_RECO_THRESHOLDS;

  return VALORIZATION_RECOMMENDATION_CATALOG.map((item) => ({
    id: item.id,
    severity: item.severity,
    scenarioTitle: t(item.scenarioTitleKey),
    scenarioDescription: t(item.scenarioDescriptionKey),
    criterion: t(item.criterionKey, {
      sortingErrorHigh: SORTING_ERROR_HIGH,
      shareHigh: SHARE_HIGH_IMPACT,
      sortingGainHigh: SORTING_GAIN_HIGH,
      objectiveGapHigh: OBJECTIVE_GAP_HIGH,
    }),
    example: item.exampleKey
      ? t(item.exampleKey, {
          sortingErrorHigh: SORTING_ERROR_HIGH,
          shareHigh: SHARE_HIGH_IMPACT,
          sortingGainHigh: SORTING_GAIN_HIGH,
          objectiveGapHigh: OBJECTIVE_GAP_HIGH,
        })
      : null,
    recommendation: t(item.recommendationKey, {
      sortingErrorHigh: SORTING_ERROR_HIGH,
      shareHigh: SHARE_HIGH_IMPACT,
      sortingGainHigh: SORTING_GAIN_HIGH,
      objectiveGapHigh: OBJECTIVE_GAP_HIGH,
    }),
  }));
};

const ImpactBadge = ({ severity, t }) => (
  <span
    className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap ${
      SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.watch
    }`}
  >
    {t(SEVERITY_LABELS[severity] ?? SEVERITY_LABELS.watch)}
  </span>
);

const RecommendationTypesTable = ({ title, rows, t }) => (
  <div className="settings-list-shell">
    <h5 className="font-semibold text-base text-blue mb-3">{title}</h5>
    <Card className="table">
      <StandardTable
        fluidColCount={4}
        fixedColWidth={0}
        columnAlignments={["left", "left", "center", "left"]}
        header={
          <div className="table-standard-header">
            <div className="table-standard-header-cell">
              {t("recommendationType.column.scenario")}
            </div>
            <div className="table-standard-header-cell">
              {t("recommendationType.column.criterion")}
            </div>
            <div className="table-standard-header-cell">
              {t("recommendationType.column.impact")}
            </div>
            <div className="table-standard-header-cell">
              {t("recommendationType.column.recommendation")}
            </div>
          </div>
        }
      >
        {rows.map((row) => (
          <div key={row.id} className="table-standard-row">
            <div className="table-standard-row-cell">
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-blue">
                  {row.scenarioTitle}
                </span>
                {row.scenarioDescription ? (
                  <span className="char-text-14 text-grey">
                    {row.scenarioDescription}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="table-standard-row-cell">
              <div className="flex flex-col gap-2">
                <span className="char-text-14 text-grey">{row.criterion}</span>
                {row.example ? (
                  <span className="self-start char-text-14 text-grey bg-light-grey rounded-md px-2 py-1">
                    {row.example}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="table-standard-row-cell">
              <ImpactBadge severity={row.severity} t={t} />
            </div>
            <div className="table-standard-row-cell">
              <span className="char-text-14 text-grey">
                {row.recommendation}
              </span>
            </div>
          </div>
        ))}
      </StandardTable>
    </Card>
  </div>
);

const ValorizationFormulasPanel = ({ t }) => (
  <div className="bg-light-grey border border-gainsboro rounded-lg p-4">
    <h6 className="font-semibold text-sm text-blue mb-2">
      {t("valorizationRecoGuide.formulasTitle")}
    </h6>
    <ul className="m-0 p-0 list-none space-y-2">
      {VALORIZATION_FORMULA_ROWS.map((row) => (
        <li key={row.id} className="text-sm text-grey">
          <span className="font-medium text-blue">
            {t(row.labelKey)}
            {" : "}
          </span>
          <span>{t(row.formulaKey)}</span>
        </li>
      ))}
    </ul>
  </div>
);

const RotationsGuideSection = ({ t }) => {
  const rows = useMemo(() => getRotationRecommendationTypeRows(t), [t]);
  return (
    <div className="flex flex-col gap-8">
      <p className="text-sm text-grey m-0">
        {t("recommendationType.pageIntro")}
      </p>
      <RecommendationTypesTable
        title={t("recommendationType.tableTitle")}
        rows={rows}
        t={t}
      />
    </div>
  );
};

const ValorizationGuideSection = ({ t }) => {
  const rows = useMemo(() => getValorizationRecommendationTypeRows(t), [t]);
  return (
    <div className="flex flex-col gap-8">
      <p className="text-sm text-grey m-0">
        {t("valorizationRecoGuide.pageIntro")}
      </p>
      <RecommendationTypesTable
        title={t("valorizationRecoGuide.tableTitle")}
        rows={rows}
        t={t}
      />
      <ValorizationFormulasPanel t={t} />
      <p className="text-xs text-grey m-0">
        {t("Valorization recommendations filter note")}
      </p>
    </div>
  );
};

/**
 * Sub-page registry keyed by path segment
 * (/statistics/recommendations/:section). Soft lookup — unknown section → null.
 */
const GUIDE_SECTION_VIEWS = {
  rotations: RotationsGuideSection,
  valorization: ValorizationGuideSection,
};

const StatisticsRecommendationsGuide = ({ section = null }) => {
  const { t } = useTranslation();
  const SectionView = GUIDE_SECTION_VIEWS[section];
  if (!SectionView) return null;
  return <SectionView t={t} />;
};

export default StatisticsRecommendationsGuide;
